/**
 * As abas do CRM no mesmo navegador combinam entre si (#536, #674).
 *
 * ── O defeito ────────────────────────────────────────────────────────────
 *
 * Cada aba aberta tem o seu fluxo SSE, a sua pilha e o seu sino. Com três
 * abas, a mesma mensagem tocava o som três vezes; o card dispensado numa
 * continuava nas outras até a próxima mensagem chegar; e cada aba escondida
 * criava o seu aviso do sistema.
 *
 * ── As duas peças ────────────────────────────────────────────────────────
 *
 *   - **Web Locks** (`navigator.locks`): para "só uma aba faz". A aba que pega
 *     a trava faz e a segura por um tempo; as outras pedem com `ifAvailable`,
 *     não esperam, e desistem.
 *   - **BroadcastChannel**: para "uma aba conta às outras". O card que saiu
 *     numa aba sai em todas na hora, sem esperar o servidor.
 *
 * As duas ficam no navegador e na mesma origem: nada sai da máquina, e só o
 * ID da notificação viaja no canal — nunca nome nem mensagem.
 *
 * ── Sem as APIs, o de antes ──────────────────────────────────────────────
 *
 * Navegador sem Web Locks (Safari antigo) se comporta como se cada aba fosse
 * a única: faz tudo, como antes. Tocar duas vezes é melhor que não tocar.
 */

const CANAL = 'cliqcrm:notificacoes';

/** A trava do som: um toque por mensagem, em qualquer número de abas. */
export const TRAVA_DO_SOM = 'cliqcrm:notificacoes:som';

/** A trava do aviso do sistema é por notificação: rajada não se perde. */
export function travaDoAviso(notificacaoId: string): string {
  return `cliqcrm:notificacoes:aviso:${notificacaoId}`;
}

function travas(): LockManager | undefined {
  return typeof navigator === 'undefined' ? undefined : navigator.locks;
}

/**
 * Pede a trava sem esperar por ela. `true`: esta aba faz, e a trava fica
 * presa por `segurarMs` — o tempo em que as outras, recebendo o mesmo evento
 * um pouco depois, ainda a encontram ocupada.
 */
export function reivindicar(nome: string, segurarMs: number): Promise<boolean> {
  const gerente = travas();
  if (!gerente) return Promise.resolve(true);
  return new Promise((resolver) => {
    gerente
      .request(nome, { ifAvailable: true }, async (trava) => {
        resolver(trava !== null);
        if (trava) await new Promise((fim) => setTimeout(fim, segurarMs));
      })
      .catch(() => resolver(true));
  });
}

// ── O que saiu da tela ─────────────────────────────────────────────────────

interface MensagemDasVistas {
  readonly tipo: 'vistas';
  readonly ids: readonly string[];
}

function ehMensagemDasVistas(dado: unknown): dado is MensagemDasVistas {
  const talvez = dado as Partial<MensagemDasVistas> | null;
  return (
    talvez?.tipo === 'vistas' &&
    Array.isArray(talvez.ids) &&
    talvez.ids.every((id) => typeof id === 'string')
  );
}

function abrirCanal(): BroadcastChannel | null {
  return typeof BroadcastChannel === 'undefined' ? null : new BroadcastChannel(CANAL);
}

/** Conta às outras abas que estes cards saíram. A própria aba não recebe. */
export function anunciarVistas(ids: readonly string[]): void {
  if (ids.length === 0) return;
  const canal = abrirCanal();
  if (!canal) return;
  try {
    canal.postMessage({ tipo: 'vistas', ids: [...ids] } satisfies MensagemDasVistas);
  } finally {
    canal.close();
  }
}

/** Ouve os cards que saíram em outra aba. Devolve o desligar. */
export function ouvirVistas(aoReceber: (ids: readonly string[]) => void): () => void {
  const canal = abrirCanal();
  if (!canal) return () => undefined;
  canal.onmessage = (evento: MessageEvent) => {
    if (ehMensagemDasVistas(evento.data)) aoReceber(evento.data.ids);
  };
  return () => canal.close();
}
